"use client";

import { ReactNode, useEffect, useState, useSyncExternalStore } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { openDonateModal } from "@/lib/donate";
import SiteFooter from "@/components/SiteFooter";
import PhoneLink from "@/components/PhoneLink";
import { readSession } from "@/lib/session";
import { usePullToReload } from "@/lib/usePullToReload";

function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "AV";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
}

function endSession(event: { preventDefault: () => void }) {
  event.preventDefault();
  window.localStorage.removeItem("token");
  fetch("http://localhost:5002/api/auth/logout", { method: "POST", credentials: "include" }).finally(() => {
    window.location.replace("/login");
  });
}

function sessionLabel() {
  const session = readSession();
  const role = session?.role === "committee" ? "Committee" : "Member";
  return `${session?.full_name || ""}|${role}`;
}

function Icon({ d }: { d: string }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d={d} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function BellIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M6 16h12l-1.2-2.2A6 6 0 0 1 16 9.2V8a4 4 0 0 0-8 0v1.2a6 6 0 0 1-.8 4.6L6 16Zm4 2a2 2 0 0 0 4 0"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const menuLinks = [
  { href: "/", label: "Dashboard", icon: "M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-5v-6H10v6H5a1 1 0 0 1-1-1v-9.5Z" },
  { href: "/about", label: "About Us", icon: "M12 21a9 9 0 1 0-9-9 9 9 0 0 0 9 9ZM12 11v5M12 8h.01" },
  { href: "/committees", label: "Committees", icon: "M16 19v-1a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v1M9.5 7.5a3 3 0 1 0-3 3 3 3 0 0 0 3-3ZM20 19v-1a3.5 3.5 0 0 0-2.7-3.4M16.5 4.6a3 3 0 0 1 0 5.8" },
  { href: "/directory", label: "Directory", icon: "M4 5.5A2.5 2.5 0 0 1 6.5 3H20v16H6.5A2.5 2.5 0 0 0 4 21.5ZM4 5.5V21.5" },
  { href: "/gallery", label: "Gallery", icon: "M4 6.5A1.5 1.5 0 0 1 5.5 5h13A1.5 1.5 0 0 1 20 6.5v11a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 17.5ZM4 15l4-4 3 3 2.5-2.5L20 16M9 9.5h.01" },
  { href: "/events", label: "Events", icon: "M7 3v3M17 3v3M4 8h16M5 5h14a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z" },
  { href: "/donations", label: "My Donations", icon: "M12 20s-7-4.4-7-9a4 4 0 0 1 7-2 4 4 0 0 1 7 2c0 4.6-7 9-7 9Z" },
  { href: "/contact", label: "Contact Us", icon: "M4 6.5h16v11H4v-11Zm0 0 8 6.5 8-6.5" },
];

const balanceSheetLink = {
  href: "/balance-sheet",
  label: "Balance Sheet",
  icon: "M5 4h14v16H5zM5 9h14M5 14h14M10 4v16",
};

function linksForRole(role: string) {
  if (role !== "committee") return menuLinks;
  const links = [...menuLinks];
  const contactIndex = links.findIndex((link) => link.href === "/contact");
  const insertAt = contactIndex >= 0 ? contactIndex : links.length;
  links.splice(insertAt, 0, balanceSheetLink);
  return links;
}

function isActive(href: string, pathname: string) {
  if (href === "/") return pathname === "/" || pathname === "/dashboard";
  if (href === "/events") return pathname === "/events" || pathname.startsWith("/events/") || pathname === "/m/events" || pathname.startsWith("/m/events/");
  if (href === "/donations") return pathname === "/donations" || pathname === "/m/donations";
  if (href === "/balance-sheet") return pathname === "/balance-sheet" || pathname === "/m/balance-sheet";
  return pathname === href || pathname.startsWith(`${href}/`);
}

export function MemberHeader({
  name,
  roleLabel,
  onDonate,
  menuOpen,
  onMenu,
}: {
  name: string;
  roleLabel: string;
  onDonate: () => void;
  menuOpen: boolean;
  onMenu: () => void;
}) {
  useEffect(() => {
    function closeOnOutside(event: PointerEvent) {
      const target = event.target;
      if (!(target instanceof Node)) return;
      document.querySelectorAll("details.md-user[open]").forEach((menu) => {
        if (!menu.contains(target)) menu.removeAttribute("open");
      });
    }
    document.addEventListener("pointerdown", closeOnOutside);
    return () => document.removeEventListener("pointerdown", closeOnOutside);
  }, []);

  return (
    <>
      <div className="md-top">
        <div className="md-bar">
          <span>
            <PhoneLink phone="9324261020">+91 9324261020</PhoneLink>
            &nbsp;||&nbsp;
            <PhoneLink phone="7208093020">+91 7208093020</PhoneLink>
          </span>
          <button
            type="button"
            onClick={(event) => {
              (event.currentTarget as HTMLButtonElement).blur();
              onDonate();
            }}
          >
            Donate Now
          </button>
        </div>
      </div>
      <header className="md-nav">
        <div className="md-bar md-nav-row">
          <button type="button" className="md-burger" aria-label={menuOpen ? "Close menu" : "Open menu"} aria-expanded={menuOpen} onClick={onMenu}>
            ☰
          </button>
          <Link href="/" className="md-brand">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/assets/Common/Website_Logo.png" alt="AVT Kalamboli" />
            <span>
              <strong>AVT <em>KALAMBOLI</em></strong>
              <small>Agroha Vikas Trust</small>
            </span>
          </Link>
          <div className="md-tools">
            <a className="md-bell" href="#member-updates" aria-label="Latest updates"><BellIcon /></a>
            <details className="md-user">
              <summary>
                <span className="md-avatar">{initials(name)}</span>
                <span className="md-user-copy">
                  <strong>{name || "Member"}</strong>
                  <small>{roleLabel}</small>
                </span>
                <span aria-hidden="true">▼</span>
              </summary>
              <div className="md-menu">
                <Link href="/profile" onClick={(event) => event.currentTarget.closest("details")?.removeAttribute("open")}>My Profile</Link>
                <button type="button" onClick={endSession}>Logout</button>
              </div>
            </details>
          </div>
        </div>
      </header>
    </>
  );
}

function SideLinks({ pathname, onNavigate }: { pathname: string; onNavigate: () => void }) {
  const [hydrated, setHydrated] = useState(false);
  const [role, setRole] = useState("");
  useEffect(() => {
    setHydrated(true);
    setRole(readSession()?.role || "");
  }, []);

  return (
    <nav className="md-side-nav">
      {linksForRole(role).map((link) => (
        <Link
          key={link.href}
          href={link.href}
          className={hydrated && isActive(link.href, pathname) ? "is-on" : undefined}
          onClick={onNavigate}
        >
          <Icon d={link.icon} />
          {link.label}
        </Link>
      ))}
    </nav>
  );
}

export function LatestUpdatesBar({ message }: { message: string }) {
  const today = useSyncExternalStore(
    () => () => {},
    () => new Date().toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" }),
    () => ""
  );

  return (
    <div className="md-ticker" id="member-updates">
      <b>Latest Updates</b>
      <p>{message || "No updates right now."}</p>
      {today ? <span className="md-ticker-date">{today}</span> : null}
    </div>
  );
}

export function MemberFooter() {
  return <SiteFooter />;
}

export default function MemberLayout({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const [menuOpen, setMenuOpen] = useState(false);
  const sessionText = useSyncExternalStore(() => () => {}, sessionLabel, () => "|Member");
  const [sessionName, sessionRole] = [sessionText.slice(0, sessionText.indexOf("|")), sessionText.slice(sessionText.indexOf("|") + 1)];
  const [profileName, setProfileName] = useState("");
  const [updates, setUpdates] = useState<string[]>([]);
  const name = profileName || sessionName;
  const roleLabel = sessionRole || "Member";

  usePullToReload(".md-frame");

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    function onResize() {
      if (window.innerWidth > 980) setMenuOpen(false);
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const chrome = document.querySelector(".md-chrome") as HTMLElement | null;
    const root = document.querySelector(".md-dash") as HTMLElement | null;
    if (!chrome || !root) return;

    function syncChromeHeight() {
      const height = Math.ceil(chrome.getBoundingClientRect().height);
      if (height > 0) {
        root.style.setProperty("--md-chrome", `${height}px`);
        document.documentElement.style.setProperty("--md-chrome", `${height}px`);
      }
    }

    syncChromeHeight();
    const observer = new ResizeObserver(syncChromeHeight);
    observer.observe(chrome);
    window.addEventListener("resize", syncChromeHeight);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", syncChromeHeight);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch("http://localhost:5002/api/profile", { credentials: "include" })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (cancelled || !data?.user) return;
        const nextName = String(data.user.full_name || data.user.username || "").trim();
        if (nextName) setProfileName(nextName);
      })
      .catch(() => {});
    fetch("http://localhost:5002/api/updates")
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (cancelled || !data?.success || !Array.isArray(data.updates)) return;
        setUpdates(data.updates.map((item: { message?: string }) => String(item.message || "").trim()).filter(Boolean));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="md-dash">
      <style>{`
        .md-dash {
          --md-orange: #F4510B;
          --md-navy: #1B2A4A;
          --md-cream: #FBF6EF;
          --md-chrome: 160px;
          height: 100dvh;
          max-height: 100dvh;
          min-height: 100dvh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          background:
            radial-gradient(circle at 100% 0%, rgba(244,81,11,0.06), transparent 28%),
            var(--md-cream);
          color: var(--md-navy);
          font-family: Poppins, sans-serif;
        }
        html:has(.md-dash),
        body:has(.md-dash) {
          height: 100% !important;
          max-height: 100dvh !important;
          margin: 0 !important;
          overflow: hidden !important;
        }
        .md-chrome {
          position: relative;
          flex: 0 0 auto;
          z-index: 50;
          width: 100%;
          background: #fff;
          box-shadow: 0 8px 18px rgba(27, 42, 74, 0.08);
          isolation: isolate;
        }
        .md-chrome a,
        .md-chrome button,
        .md-chrome summary {
          position: relative;
          z-index: 2;
          pointer-events: auto;
          cursor: pointer;
          touch-action: manipulation;
        }
        .md-top a {
          color: #fff;
          text-decoration: none;
          border-bottom: 1px solid rgba(255,255,255,0.35);
        }
        .md-top a:hover { border-bottom-color: #fff; }
        .md-top .phone-action,
        .md-top .phone-action-trigger {
          color: #fff;
          opacity: 1;
        }
        .md-top .phone-action-trigger {
          border-bottom: none;
          text-decoration-color: rgba(255,255,255,0.55);
        }
        .md-top .phone-action-trigger:hover,
        .md-top .phone-action.is-open .phone-action-trigger {
          text-decoration-color: #fff;
        }
        .md-bar {
          width: calc(100% - 40px);
          margin: 0 auto;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          min-height: 36px;
          box-sizing: border-box;
        }
        .md-top button, .md-logout {
          border: none;
          background: transparent !important;
          color: #fff !important;
          font: inherit;
          font-weight: 700;
          cursor: pointer;
          box-shadow: none !important;
          outline: none !important;
          -webkit-tap-highlight-color: transparent;
          -webkit-appearance: none;
          appearance: none;
        }
        .md-top button { border-bottom: 1px solid rgba(255,255,255,0.45); padding: 2px 0; }
        .md-top button:hover,
        .md-top button:focus,
        .md-top button:focus-visible,
        .md-top button:active {
          color: #fff !important;
          background: transparent !important;
          outline: none !important;
          box-shadow: none !important;
        }
        .md-nav { background: #fff; border-bottom: 1px solid #EFE4D6; }
        .md-nav-row { min-height: 74px; padding: 8px 0; display: flex; align-items: center; gap: 18px; }
        .md-brand { display: flex; align-items: center; gap: 10px; text-decoration: none; }
        .md-brand img { width: 48px; height: 48px; border-radius: 50%; object-fit: contain; background: #fff; }
        .md-brand strong { display: block; font-family: var(--font-head); color: var(--md-navy); font-size: 18px; line-height: 1.05; }
        .md-brand strong em { font-style: normal; color: var(--md-orange); }
        .md-brand small { display: block; margin-top: 2px; color: #8A7466; font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; font-weight: 650; }
        .md-tools { margin-left: auto; display: flex; align-items: center; gap: 10px; }
        .md-bell {
          width: 38px; height: 38px; border-radius: 50%; border: 1px solid #EFE4D6;
          display: grid; place-items: center; text-decoration: none; background: #fff;
          color: #111111;
        }
        .md-bell:hover, .md-bell:focus-visible {
          border-color: #F0C39A;
          color: #111111;
          outline: none;
        }
        .md-user { position: relative; }
        .md-user summary { list-style: none; display: flex; align-items: center; gap: 10px; cursor: pointer; }
        .md-user summary::-webkit-details-marker { display: none; }
        .md-menu {
          position: absolute; right: 0; top: calc(100% + 8px); z-index: 30;
          min-width: 180px; padding: 6px; background: #fff;
          border: 1px solid #EFE4D6; border-radius: 12px;
          box-shadow: 0 12px 28px rgba(27,42,74,0.08);
        }
        .md-menu a, .md-menu button {
          display: block; width: 100%; text-align: left; border: none; background: transparent;
          color: var(--md-navy); text-decoration: none; font: inherit; font-size: 14px; font-weight: 600;
          padding: 10px 12px; border-radius: 8px; cursor: pointer;
        }
        .md-menu a:hover, .md-menu button:hover { background: #FFF6EE; }
        .md-avatar {
          width: 40px; height: 40px; border-radius: 50%;
          background: #FFF1E6; color: var(--md-orange); border: 1.5px solid #F0C39A;
          display: grid; place-items: center; font-weight: 750;
        }
        .md-user strong { display: block; font-size: 13.5px; color: var(--md-navy); }
        .md-user small { display: block; color: #8D7568; font-size: 12px; }
        .md-logout {
          background: var(--md-orange);
          color: #fff;
          border-radius: 8px;
          height: 36px;
          padding: 0 14px;
        }
        .md-burger { display: none; border: 1px solid #EFE4D6; background: #fff; border-radius: 10px; width: 40px; height: 40px; cursor: pointer; }
        .md-frame {
          display: grid;
          grid-template-columns: 240px minmax(0, 1fr);
          align-items: start;
          flex: 1 1 auto;
          min-height: 0;
          overflow-x: hidden;
          overflow-y: auto;
          overscroll-behavior-y: auto;
          -webkit-overflow-scrolling: touch;
          box-sizing: border-box;
        }
        .md-side {
          grid-row: 1;
          align-self: start;
          position: sticky;
          top: 0;
          height: calc(100dvh - var(--md-chrome));
          max-height: calc(100dvh - var(--md-chrome));
          overflow-x: hidden;
          overflow-y: auto;
          overscroll-behavior: contain;
          background: #fff;
          border-right: 1px solid #eadfce;
          box-sizing: border-box;
          -webkit-overflow-scrolling: touch;
        }
        .md-side-nav {
          position: static;
          display: flex;
          flex-direction: column;
          gap: 4px;
          padding: 14px 12px 28px;
          box-sizing: border-box;
        }
        .md-body { grid-row: 1; min-width: 0; }
        .md-foot { grid-column: 1 / -1; }
        .md-side a, .md-side button {
          display: flex; align-items: center; gap: 12px;
          min-height: 44px; padding: 0 14px; border-radius: 10px;
          color: #1B2A4A; text-decoration: none;
          font-family: Poppins, sans-serif; font-size: 14px; font-weight: 600; letter-spacing: 0.01em;
          background: transparent; border: none; text-align: left; cursor: pointer; width: 100%;
          box-sizing: border-box;
        }
        .md-side a svg, .md-side button svg { flex: 0 0 18px; width: 18px; height: 18px; color: #5C6B82; }
        .md-side a:hover, .md-side button:hover { background: #FFF6EE; color: #E65100; }
        .md-side a:hover svg, .md-side button:hover svg { color: #E65100; }
        .md-side a.is-on { background: var(--md-orange); color: #fff; }
        .md-side a.is-on svg { color: #fff; }
        .md-backdrop { display: none; }
        .md-ticker { display: flex; align-items: center; min-height: 40px; background: #2C3138; color: #fff; overflow: hidden; }
        .md-ticker b { display: inline-flex; align-items: center; align-self: stretch; padding: 0 16px; background: var(--md-orange); white-space: nowrap; }
        .md-ticker p { margin: 0; padding: 10px 16px; font-size: 14px; flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .md-ticker-date { padding-right: 18px; font-size: 13px; white-space: nowrap; color: #F3E6D4; }
        .md-dash .gallery-section {
          width: min(1200px, calc(100% - 40px));
          margin: 28px auto 32px;
          padding: 0;
          box-sizing: border-box;
        }
        .md-dash .gallery-grid {
          grid-template-columns: repeat(auto-fit, minmax(240px, 280px));
          justify-content: center;
        }
        .md-dash .ap-wrap { width: min(1200px, calc(100% - 40px)); }
        .md-page {
          width: min(1200px, calc(100% - 40px));
          margin: 0 auto;
          padding: 28px 0 48px;
          box-sizing: border-box;
        }
        .md-page h1 { margin: 0; font-family: var(--font-head); font-size: clamp(30px, 3vw, 36px); color: var(--md-navy); text-align: center; }
        .md-page .md-lead { margin: 8px 0 22px; text-align: center; color: #6F6258; }
        .md-event-list { display: grid; gap: 14px; }
        .md-event-card {
          display: grid; grid-template-columns: 92px minmax(0, 1fr) auto; gap: 14px; align-items: center;
          background: #fff; border: 1px solid #EFE4D6; border-radius: 16px; padding: 14px;
          box-shadow: 0 8px 20px rgba(27,42,74,0.04); text-decoration: none; color: inherit;
        }
        .md-event-card img, .md-event-fallback { width: 92px; height: 72px; object-fit: cover; border-radius: 10px; background: #FFF1E8; }
        .md-event-fallback { display: grid; place-items: center; }
        .md-event-card strong { display: block; color: var(--md-navy); }
        .md-event-card small { display: block; margin-top: 4px; color: #8D7568; font-weight: 500; }
        .md-event-card > a { color: var(--md-orange); font-weight: 700; text-decoration: none; }
        .md-section { margin: 8px 0 12px; font-size: 16px; color: var(--md-navy); }
        .md-back { margin: 0 0 8px; }
        .md-back a { color: var(--md-orange); font-weight: 700; text-decoration: none; }
        .md-photo-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 12px; }
        .md-photo-grid img { width: 100%; height: 140px; object-fit: cover; border-radius: 12px; background: #FFF1E8; }
        .md-stat-row { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; margin-bottom: 16px; }
        .md-stat-row article { background: #fff; border: 1px solid #EFE4D6; border-radius: 14px; padding: 14px; }
        .md-stat-row span { display: block; color: #8D7568; font-size: 13px; }
        .md-stat-row strong { display: block; margin-top: 6px; font-family: var(--font-body); font-weight: 700; font-size: 22px; color: var(--md-navy); }
        .md-donation-list { display: grid; gap: 10px; }
        .md-donation-row {
          display: grid; grid-template-columns: minmax(0, 1fr) auto auto auto; gap: 12px; align-items: center;
          background: #fff; border: 1px solid #EFE4D6; border-radius: 14px; padding: 14px;
        }
        .md-donation-row strong, .md-donation-row b { color: var(--md-navy); }
        .md-donation-row b { font-family: var(--font-body); font-weight: 700; }
        .md-donation-row small { display: block; margin-top: 4px; color: #8D7568; }
        .md-donation-row em { font-style: normal; font-weight: 700; font-size: 12px; border-radius: 999px; padding: 4px 10px; }
        .md-donation-row em.is-wait { background: #FFF1E6; color: #C2410C; }
        .md-donation-row em.is-ok { background: #E8F6EE; color: #157A3A; }
        .md-donation-row em.is-bad { background: #FDECEC; color: #B42318; }
        .md-donation-row em.is-muted { background: #F3F0EA; color: #6F6258; }
        .md-donation-row a { color: var(--md-orange); font-weight: 700; text-decoration: none; }
        @media (max-width: 800px) {
          .md-stat-row { grid-template-columns: 1fr 1fr; }
          .md-donation-row { grid-template-columns: 1fr 1fr; }
        }
        .md-empty-card {
          background: #fff; border: 1px solid #EFE4D6; border-radius: 16px; padding: 28px 20px;
          text-align: center; color: #6F6258; box-shadow: 0 8px 20px rgba(27,42,74,0.04);
        }
        .md-empty-card button {
          margin-top: 14px; border: none; background: var(--md-orange); color: #fff;
          border-radius: 10px; height: 44px; padding: 0 18px; font-weight: 700; cursor: pointer;
        }
        @media (max-width: 980px) {
          .md-logout, .md-user-copy { display: none; }
          .md-burger { display: grid; place-items: center; }
          .md-tools { justify-content: flex-end; }
          .md-frame { grid-template-columns: minmax(0, 1fr); }
          .md-side { display: none; }
          .md-side.is-open {
            display: block; position: fixed; z-index: 40;
            top: var(--md-chrome); left: 0; bottom: 0;
            width: min(300px, 86vw); min-height: 0; height: auto;
            overflow: auto; background: #fff;
            box-shadow: 12px 0 30px rgba(27,42,74,0.16);
            pointer-events: auto;
          }
          .md-dash:has(.md-side.is-open) .md-frame {
            overflow: hidden;
          }
          .md-backdrop {
            display: block; position: fixed;
            inset: var(--md-chrome) 0 0 0; z-index: 30; border: none;
            background: rgba(27,42,74,0.35); cursor: pointer; pointer-events: auto;
          }
          .md-ticker-date { display: none; }
          .md-event-card { grid-template-columns: 72px minmax(0, 1fr); }
          .md-event-card span { grid-column: 2; }
        }
      `}</style>
      <div className="md-chrome">
        <MemberHeader
          name={name}
          roleLabel={roleLabel}
          menuOpen={menuOpen}
          onMenu={() => setMenuOpen((open) => !open)}
          onDonate={() => openDonateModal()}
        />
        <LatestUpdatesBar message={updates.join("    •    ")} />
      </div>
      <div className="md-frame">
        <aside className={menuOpen ? "md-side is-open" : "md-side"} aria-label="Member">
          <SideLinks pathname={pathname} onNavigate={() => setMenuOpen(false)} />
        </aside>
        {menuOpen ? <button type="button" className="md-backdrop" aria-label="Close menu" onClick={() => setMenuOpen(false)} /> : null}
        <div className="md-body">{children}</div>
        <div className="md-foot"><MemberFooter /></div>
      </div>
    </div>
  );
}
